import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { selectAggregationForSelectedReport } from 'app/area/favourite/selectors';
import { FieldItem, SelectItem } from 'app/area/reports/types';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ComplexValue } from 'ui';
import { actionCreators } from './actions';

export function useActionCreators(
  api: ReportCommonApi, 
  fieldsToClearOnChange?: { target?: boolean; rollingAverage?: boolean; selections?: boolean }
) {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  const {
    setAggregateField,
    setAggregateFunction,
    ...rest
  } = bindActionCreators(actionCreators(api, instanceKey), dispatch);

  const setAggregateFieldWithClearValues = 
    (field: FieldItem, selectedInitialFunction?: SelectItem, selectedInitialUomSet?: SelectItem) => 
      setAggregateField(field, selectedInitialFunction, selectedInitialUomSet, fieldsToClearOnChange);
  
  const setAggregateFunctionWithClearValues =
    (aggregateFunction: ComplexValue<any>, uomSet: ComplexValue<any>) =>
      setAggregateFunction(aggregateFunction, uomSet, fieldsToClearOnChange);
  
  return useMemo(() => ({
    setAggregateField: setAggregateFieldWithClearValues,
    setAggregateFunction: setAggregateFunctionWithClearValues,
    ...rest
  }), [
    dispatch, api, instanceKey, 
    fieldsToClearOnChange?.target, 
    fieldsToClearOnChange?.rollingAverage, 
    fieldsToClearOnChange?.selections
  ]);
}

export function useWorkingDefinitionAggregationState() {
  const instanceKey = useInstanceKey();

  const {
    aggregationDomain,
    aggregationType,
    uomSetDomain
  } = useSelector(selectAggregationForSelectedReport(instanceKey));

  return {
    selectedAggregationField: codeItemToStoreValue(aggregationDomain),
    selectedAggregationFunction: codeItemToStoreValue(aggregationType),
    selectedUomSet: codeItemToStoreValue(uomSetDomain)
  };
}

export function useLoadInitialDataEffect(api: ReportCommonApi) {
  const {
    loadAggregateDefaultFields,
    clearState
  } = useActionCreators(api);

  useEffect(() => {
    loadAggregateDefaultFields();
    return () => {
      clearState();
    };
  }, [loadAggregateDefaultFields]);
}

export function useLoadAggregateFieldOptionsEffect(api: ReportCommonApi) {
  const instanceKey = useInstanceKey();
  
  const {
    aggregationDomain
  } = useSelector(selectAggregationForSelectedReport(instanceKey));
  
  const {
    loadAggregateFieldOptions
  } = useActionCreators(api);

  useEffect(() => {
    if (aggregationDomain?.v) {
      loadAggregateFieldOptions(aggregationDomain?.v);
    }
  }, [loadAggregateFieldOptions, aggregationDomain?.v]);
}
